import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  clearSettings,
  filterLayoutClasses,
  loadSettings,
  saveSettings,
  themeStyleFromUrl,
  themeStyleUrl,
  type ColorMode,
  type ThemeStyle,
} from './themeStorage';

/**
 * React replacement for the imperative parts of the SmartAdmin runtime —
 * `smartApp.js` (the `data-action` dispatcher), `saveloadscript.js`
 * (persistence) and the layout half of `smartNavigation.js`.
 *
 * Those scripts cannot simply be script-included: they mutate the DOM that
 * React reconciles, and two writers of the same class list will fight. The
 * behaviours the app actually uses are re-expressed here as state, with a
 * single effect writing the html element. Everything else in those 4,200 lines
 * — sound playback, panel drag/drop, chart colour re-extraction, the demo
 * stack generator — is not used by any page in this application and is not
 * ported.
 *
 * The persisted format is byte-compatible with the old one; see themeStorage.ts.
 */

/** A layout class from the design's `set-*` vocabulary. */
export type LayoutClass =
  | 'set-header-fixed'
  | 'set-nav-full'
  | 'set-nav-fixed'
  | 'set-nav-collapsed'
  | 'set-nav-minified'
  | 'set-nav-dark'
  | 'set-colorblind-mode'
  | 'set-high-contrast-mode';

/**
 * Declares which layout classes cannot be on at the same time, and which imply
 * others. This reproduces the `data-dependency` / `data-codependence`
 * attributes the settings panel and header buttons carried in the markup:
 * enabling "navigation collapsed" requires full-height navigation, and
 * enabling full-height navigation clears collapsed.
 */
const CLASS_RULES: Partial<Record<LayoutClass, { requires?: LayoutClass[]; clears?: LayoutClass[] }>> = {
  'set-nav-collapsed': { requires: ['set-nav-full'] },
  'set-nav-full': { clears: ['set-nav-collapsed'] },
};

interface LayoutContextValue {
  /** Currently applied `set-*` classes. */
  layoutClasses: ReadonlySet<LayoutClass>;
  hasLayoutClass: (cls: LayoutClass) => boolean;
  toggleLayoutClass: (cls: LayoutClass) => void;

  /** Light/dark mode, written to `data-bs-theme`. */
  colorMode: ColorMode;
  toggleColorMode: () => void;

  /** Active colour theme, or null for the default palette. */
  themeStyle: ThemeStyle | null;
  setThemeStyle: (style: ThemeStyle | null) => void;

  /** Off-canvas navigation on small screens. */
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  toggleMobileMenu: () => void;

  /** The settings drawer (`aside.js-drawer-settings`). */
  settingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
  toggleSettings: () => void;

  /** Browser fullscreen. */
  isFullscreen: boolean;
  toggleFullscreen: () => void;

  /** Clears saved settings and returns the shell to its shipped defaults. */
  resetLayout: () => void;
}

export const LayoutContext = createContext<LayoutContextValue | null>(null);

const initial = loadSettings();

function parseClasses(value: string): Set<LayoutClass> {
  return new Set(
    filterLayoutClasses(value).split(' ').filter(Boolean) as LayoutClass[],
  );
}

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [layoutClasses, setLayoutClasses] = useState<Set<LayoutClass>>(() =>
    parseClasses(initial.htmlRoot),
  );
  const [colorMode, setColorMode] = useState<ColorMode>(initial.theme);
  const [themeStyle, setThemeStyleState] = useState<ThemeStyle | null>(() =>
    themeStyleFromUrl(initial.themeStyle),
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(() => document.fullscreenElement != null);

  // One writer for the html element. Transient classes are applied here too but
  // never persisted, which is what the original `filterClass(..., /^set-/)`
  // achieved — a stuck `app-mobile-menu-open` would otherwise survive a reload
  // and leave the sidebar open over the content.
  useEffect(() => {
    const root = document.documentElement;
    const desired = [...layoutClasses];
    if (mobileMenuOpen) desired.push('app-mobile-menu-open' as LayoutClass);

    root.className = desired.join(' ');
    root.setAttribute('data-bs-theme', colorMode);
  }, [layoutClasses, mobileMenuOpen, colorMode]);

  // Swaps the `#theme-style` stylesheet among the nine prebuilt colour themes.
  // The link element is reused rather than recreated so the browser does not
  // drop to unstyled content between themes.
  useEffect(() => {
    const existing = document.getElementById('theme-style') as HTMLLinkElement | null;

    if (!themeStyle) {
      existing?.remove();
      return;
    }

    const href = themeStyleUrl(themeStyle);
    if (existing) {
      if (existing.getAttribute('href') !== href) existing.setAttribute('href', href);
      return;
    }

    const link = document.createElement('link');
    link.id = 'theme-style';
    link.rel = 'stylesheet';
    link.media = 'screen';
    link.href = href;
    document.head.appendChild(link);
  }, [themeStyle]);

  // Persist whenever any saved dimension changes.
  useEffect(() => {
    saveSettings({
      htmlRoot: [...layoutClasses].join(' '),
      theme: colorMode,
      themeStyle: themeStyle ? themeStyleUrl(themeStyle) : '',
    });
  }, [layoutClasses, colorMode, themeStyle]);

  // The user can leave fullscreen with Escape or F11 without going through our
  // button, so the browser event is the source of truth, not the click.
  useEffect(() => {
    const onChange = () => {
      const active = document.fullscreenElement != null;
      setIsFullscreen(active);
      document.body.classList.toggle('fullscreen-active', active);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleLayoutClass = useCallback((cls: LayoutClass) => {
    setLayoutClasses((current) => {
      const next = new Set(current);

      if (next.has(cls)) {
        next.delete(cls);
      } else {
        next.add(cls);
        const rules = CLASS_RULES[cls];
        rules?.requires?.forEach((dep) => next.add(dep));
        rules?.clears?.forEach((dep) => next.delete(dep));
      }

      return next;
    });
  }, []);

  const toggleColorMode = useCallback(() => {
    setColorMode((current) => (current === 'light' ? 'dark' : 'light'));
  }, []);

  const setThemeStyle = useCallback((style: ThemeStyle | null) => {
    setThemeStyleState(style);
  }, []);

  const toggleMobileMenu = useCallback(() => setMobileMenuOpen((open) => !open), []);

  const toggleSettings = useCallback(() => setSettingsOpen((open) => !open), []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void document.documentElement.requestFullscreen().catch(() => undefined);
    }
  }, []);

  const resetLayout = useCallback(() => {
    clearSettings();
    setLayoutClasses(new Set());
    setColorMode('light');
    setThemeStyleState(null);
    setMobileMenuOpen(false);
    setSettingsOpen(false);
  }, []);

  const value = useMemo<LayoutContextValue>(
    () => ({
      layoutClasses,
      hasLayoutClass: (cls) => layoutClasses.has(cls),
      toggleLayoutClass,
      colorMode,
      toggleColorMode,
      themeStyle,
      setThemeStyle,
      mobileMenuOpen,
      setMobileMenuOpen,
      toggleMobileMenu,
      settingsOpen,
      setSettingsOpen,
      toggleSettings,
      isFullscreen,
      toggleFullscreen,
      resetLayout,
    }),
    [
      layoutClasses,
      toggleLayoutClass,
      colorMode,
      toggleColorMode,
      themeStyle,
      setThemeStyle,
      mobileMenuOpen,
      toggleMobileMenu,
      settingsOpen,
      toggleSettings,
      isFullscreen,
      toggleFullscreen,
      resetLayout,
    ],
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}
